"use client";

import { Loader2 } from "lucide-react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { useState } from "react";
import { toast } from "sonner";
import { z } from "zod";

import { Button } from "@workspace/ui/components/button";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@workspace/ui/components/card";

import { signIn } from "@/lib/auth-client";
import { InputField } from "@workspace/ui/components/input";
import { useSlyderzForm } from "app/hooks/useSlyderzForm";

export const loginSchema = z.object({
	email: z.string().email(),
	password: z.string(),
});

export default function LoginForm() {
	const router = useRouter();
	const [loading, setLoading] = useState(false);

	const handleFormSubmit = async (input: z.infer<typeof loginSchema>) => {
		await signIn.email(
			{
				email: input.email,
				password: input.password,
			},
			{
				onResponse: () => {
					setLoading(false);
				},
				onRequest: () => {
					setLoading(true);
				},
				onSuccess: async ({ data }: { data: any }) => {
					router.push("/");
				},
				onError: (ctx: any) => {
					console.log(ctx.error);
					toast.error(ctx.error.message);
				},
			},
		);
	};

	const {
		handleSubmit,
		register,
		formState: { errors },
	} = useSlyderzForm(loginSchema, {
		email: "",
		password: "",
	});

	return (
		<form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-8 mt-16">
			<Card className="max-w-md">
				<CardHeader>
					<CardTitle className="text-lg md:text-xl">Welcome back</CardTitle>
					<CardDescription className="text-xs md:text-sm">
						Enter your email below to login to your account
					</CardDescription>
				</CardHeader>
				<CardContent>
					<div className="grid gap-4">
						<div className="grid gap-2">
							<InputField
								{...register("email")}
								label="Email"
								placeholder="m@example.com"
								autoComplete="email"
								error={errors?.email?.message}
							/>
						</div>

						<div className="grid gap-2">
							<div className="flex items-center">
								<Link
									href="/forgot-password"
									className="ml-auto inline-block text-sm underline"
								>
									Forgot your password?
								</Link>
							</div>

							<InputField
								{...register("password")}
								label="Password"
								placeholder="Enter your password"
								autoComplete="password"
								type="password"
								error={errors?.password?.message}
							/>
						</div>

						<Button type="submit" className="w-full" disabled={loading}>
							{loading ? (
								<Loader2 size={16} className="animate-spin" />
							) : (
								"Login"
							)}
						</Button>

						<div className="flex items-center">
							<p className="ml-4 text-center text-sm text-gray-500">
								Not a member?{" "}
								<Link
									href="/signup"
									className="font-semibold leading-6 text-indigo-600 hover:text-indigo-500"
								>
									Sign up
								</Link>
							</p>
						</div>
					</div>
				</CardContent>
			</Card>
		</form>
	);
}
